import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronDown, MessageSquare, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" }) + " · " + d.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" });
};

export function LeadsTab({ token }) {
  const [mode, setMode] = useState("quotes");
  const [quotes, setQuotes] = useState(null);
  const [chats, setChats] = useState(null);
  const [openSession, setOpenSession] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/quotes`, { headers }).then((r) => setQuotes(r.data)).catch(() => toast.error("Could not load quotes"));
    axios.get(`${API}/chats`, { headers }).then((r) => setChats(r.data)).catch(() => toast.error("Could not load chats"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="admin-leads">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold">Leads</h1>
        <div className="flex items-center rounded-full border border-slate-200 p-0.5">
          {[
            ["quotes", "Quote Requests", FileText],
            ["chats", "Chat Transcripts", MessageSquare],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`admin-leads-mode-${id}`}
              onClick={() => setMode(id)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                mode === id ? "bg-navy text-white" : "text-slate-400 hover:text-ink"
              }`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
      </div>

      {mode === "quotes" &&
        (quotes === null ? (
          <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-navy" /></div>
        ) : quotes.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">No quote requests yet.</p>
        ) : (
          <div className="space-y-3">
            {quotes.map((q, i) => (
              <div key={q.id || i} data-testid={`admin-quote-row-${i}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-navy font-display text-xs font-semibold text-aqua-300">
                      {q.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <div className="text-sm font-bold text-ink">{q.name}</div>
                      <div className="text-xs text-slate-400">{q.email}{q.phone ? ` · ${q.phone}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-navy/5 px-3 py-1 text-[11px] font-bold text-navy">{q.transaction_type}</span>
                    <span className="rounded-full bg-aqua-600/10 px-3 py-1 text-[11px] font-bold text-aqua-700">{q.province}</span>
                    <span className="text-[11px] text-slate-400">{fmt(q.created_at)}</span>
                  </div>
                </div>
                {q.message && <p className="mt-3 border-t border-slate-200 pt-3 text-sm text-slate-600">{q.message}</p>}
              </div>
            ))}
          </div>
        ))}

      {mode === "chats" &&
        (chats === null ? (
          <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-navy" /></div>
        ) : chats.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">No chat conversations yet.</p>
        ) : (
          <div className="space-y-3">
            {chats.map((s, i) => (
              <div key={s.session_id} data-testid={`admin-chat-session-${i}`} className="rounded-2xl border border-slate-200 bg-slate-50">
                <button
                  data-testid={`admin-chat-expand-${i}`}
                  onClick={() => setOpenSession(openSession === s.session_id ? null : s.session_id)}
                  className="flex w-full items-center justify-between gap-3 p-4 text-left"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold text-ink">{s.preview}</div>
                    <div className="text-xs text-slate-400">
                      {s.message_count} messages · last active {fmt(s.last_at)}
                    </div>
                  </div>
                  <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform duration-200 ${openSession === s.session_id ? "rotate-180" : ""}`} />
                </button>
                {openSession === s.session_id && (
                  <div className="space-y-2 border-t border-slate-200 p-4">
                    {s.messages.map((m, j) => (
                      <div key={j} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                            m.role === "user" ? "rounded-br-md bg-navy text-white" : "rounded-bl-md border border-slate-200 bg-white text-ink"
                          }`}
                        >
                          {m.content}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}
